import { Link } from "@mui/material";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { useGetMachineComponents } from "hooks/machines/useGetMachineComponents";
import { generateGuid } from "core/utils/str";
import PropTypes from "prop-types";
import React, { useContext, useState } from "react";
import { CaseFormDialog } from "components/AlertsManagement/Case/form/CaseFormDialog";
import { styled } from "@mui/material/styles";
import { useGetSiteById } from "hooks/sites/useGetSiteById";
import { useGetMachineById } from "hooks/machines/useGetMachineById";
import { usePostAlertCase } from "hooks/alerts/usePostAlertCase";

export const CreateCaseDialog = ({
  open,
  onClose,
  onCaseCreateSuccess,
  alert: {
    alert_id: alertId,
    site_id: siteId,
    component_id: initialComponentId,
    machine_id: machineId,
    tag_id: tagId,
    tag_name: tagName,
    tag_unit: tagUnit,
    rule_id: ruleId,
    rule_name: ruleName,
    severity_type_id: severityTypeId,
    severity_type_name: severityTypeName,
    recommendation
  }
}) => {
  const { loading, error, postCaseCallback } = usePostAlertCase(alertId);
  const [componentId, setComponentId] = useState(initialComponentId);

  const { loading: siteLoading, error: siteError, site } = useGetSiteById(
    siteId
  );

  const {
    loading: machineLoading,
    error: machineError,
    machine
  } = useGetMachineById(machineId);

  const {
    componentsLoading,
    componentsError,
    components
  } = useGetMachineComponents(machine);

  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);

  const handleOnChangeComponent = component => {
    setComponentId(component?.id ?? null);
  };

  const handleCaseCreated = response => {
    onClose();
    onCaseCreateSuccess();
    addSnackbarToQueue({
      severity: "success",
      message: (
        <SnackbarContent>
          Case created successfully.
          <Link
            href={`/pages/case-management/${response.case}`}
            color="inherit"
            underline="always"
            target="_blank"
            rel="noopener noreferrer"
          >
            View Case
          </Link>
        </SnackbarContent>
      ),
      autoHideDuration: 6000
    });
  };

  const handleSubmit = ({
    description,
    open_date,
    component: { id: componentId },
    diagnostic
  }) => {
    const currentDate = new Date();
    const currentHour = currentDate.getUTCHours();
    const currentMinutes = currentDate.getUTCMinutes();
    const currentSeconds = currentDate.getUTCSeconds();

    const openDate = new Date(open_date);
    openDate.setUTCHours(currentHour, currentMinutes, currentSeconds);

    const tempCase = {
      name: generateGuid(),
      description,
      machine: machineId,
      component: componentId,
      open_date: openDate.toISOString(),
      priority: 1,
      estimated_saving: 0,
      machine_status: 1
    };

    const tempDiagnostic = {
      ...diagnostic,
      component: componentId,
      date_time: diagnostic.date_time.toISOString()
    };

    postCaseCallback({
      case: tempCase,
      event: tempDiagnostic
    }).then(handleCaseCreated, _error => {});
  };

  return (
    <CaseFormDialog
      open={open}
      loading={loading}
      error={error}
      site={site}
      siteLoading={siteLoading}
      siteError={siteError}
      machine={machine}
      machineLoading={machineLoading}
      machineError={machineError}
      componentId={componentId}
      components={components}
      onChangeComponent={handleOnChangeComponent}
      componentsError={componentsError}
      componentsLoading={componentsLoading}
      initialValues={{
        site: null,
        description: `Rule ${ruleName} moved to ${severityTypeName}`,
        open_date: new Date(),
        component: null,
        diagnostic: {
          date_time: new Date(),
          type: 1,
          case: null,
          case_severity_type: severityTypeId,
          description: `Rule ${ruleName} moved to ${severityTypeName}`,
          recommendation: recommendation ?? "",
          params: {
            feedComment: [],
            alertTimeSeriesMetadata: {
              tagId,
              tagName,
              tagUnit,
              machineId,
              severityTypeId,
              ruleId,
              ruleName
            }
          }
        }
      }}
      onClose={onClose}
      onSubmit={handleSubmit}
    />
  );
};

CreateCaseDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  alert: PropTypes.object,
  onCaseCreateSuccess: PropTypes.func
};

const SnackbarContent = styled("div")`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(2)};
`;
